import {MenuIcon, XIcon} from 'lucide-react';
import {useState} from 'react';
import {NavLink} from 'react-router';

import {ThemeToggle} from './theme-toggle.js';
import {Button} from '@/components/ui/button.js';
import {cn} from '@/lib/cn.js';

/** One navigation destination. */
export interface NavItem {
  to: string;
  label: string;
}

/** Props accepted by {@link MobileNavMenu}. */
export interface MobileNavMenuProps {
  items: readonly NavItem[];
}

/**
 * A collapsible menu for narrow viewports, where the full horizontal nav
 * row does not fit even after hiding the wordmark. Shown only below `sm`;
 * the ordinary row in {@link SiteHeader} handles `sm` and up.
 *
 * @param props The destinations to list.
 * @returns The rendered toggle button and, when open, the menu panel.
 */
export function MobileNavMenu({items}: MobileNavMenuProps): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="sm:hidden">
      <Button
        variant="ghost"
        size="icon"
        aria-expanded={isOpen}
        aria-controls="mobile-nav-panel"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        onClick={() => {
          setIsOpen((previous) => !previous);
        }}
      >
        {isOpen ? <XIcon /> : <MenuIcon />}
      </Button>

      {isOpen ? (
        <nav
          id="mobile-nav-panel"
          aria-label="Main"
          className="absolute inset-x-0 top-14 z-30 border-b border-border bg-background px-4 py-3 shadow-sm"
        >
          <ul className="flex flex-col gap-1">
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  onClick={() => {
                    setIsOpen(false);
                  }}
                  className={({isActive}) =>
                    cn(
                      'block rounded-md px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-muted font-medium text-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="mt-2 border-t border-border pt-2">
            <ThemeToggle />
          </div>
        </nav>
      ) : undefined}
    </div>
  );
}
